// ============================================================
// FavouritesErrorToast.tsx
// Small fixed toast for favourites errors — notably the 20-item cap
// ("Favourites limit reached (20). Remove one to add another."). The
// favourites star can be clicked from several surfaces (case / client /
// task headers, list rows), so a single global toast is clearer than a
// per-star inline message. Reads the store's favouritesError slot, which
// toggleFavourite sets on failure (after reverting the optimistic fill).
//
// The fixed toast wrapper holds the shared slim ActionBox (red `danger`
// tone) — the same note shell as everywhere else. Auto-dismisses after 4s;
// also dismissible via its "Dismiss" link for keyboard users.
// ============================================================
import React, { useEffect } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import ActionBox from './ActionBox'

const FavouritesErrorToast: React.FC = () => {
  const favouritesError = useCasevoStore((s) => s.favouritesError)
  const setFavouritesError = useCasevoStore((s) => s.setFavouritesError)

  // Auto-dismiss. Keyed on the message so each new error restarts the
  // 4s timer; cleared on unmount / message change.
  useEffect(() => {
    if (!favouritesError) return
    const timer = window.setTimeout(() => setFavouritesError(null), 4000)
    return () => window.clearTimeout(timer)
  }, [favouritesError, setFavouritesError])

  if (!favouritesError) return null

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] max-w-[90vw]"
      role="status"
      aria-live="polite"
    >
      <ActionBox variant="slim" tone="danger" ariaLabel="Favourites" className="shadow-lg"
        message={favouritesError}
        actions={[{ label: 'Dismiss', kind: 'link', onClick: () => setFavouritesError(null) }]} />
    </div>
  )
}

export default FavouritesErrorToast
